import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Card,
  CardBody,
  Col,
  Label,
  Input,
  FormFeedback,
  Spinner,
  Form,
} from "reactstrap";
import * as Yup from "yup";
import Select from "react-select";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import Flatpickr from "react-flatpickr";

import { BreadCrumb } from "../../components/Common";
import { State, SendNotificationRequest, TemplateDto } from "../../interfaces";
import {
  NotificationAudienceEnum,
  NotificationMethodEnum,
  OccurrenceEnum,
} from "../../enums";
import { resetSendNotification, sendNotification } from "../../store/actions";
import { Link, useHistory } from "react-router-dom";
import { TemplateSelect } from "../../components";

const NotifyUsersPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  document.title = `${t("documentTitle", { pageName: "notifyUsers" })}`;
  const [nowOrLater, setNowOrLater] = useState<OccurrenceEnum>(
    OccurrenceEnum.NOW
  );

  //#region internal state
  const [sendMethod, setSendMethod] = useState<NotificationMethodEnum>(
    NotificationMethodEnum.WHATSAPP
  );
  const [selectedTemplate, setSelectedTemplate] = useState<any>();
  //#endregion

  //#region redux state
  const { response: templates } = useSelector(
    (state: any): State<TemplateDto[]> => state.template.templates
  );
  //#endregion

  //#region notification
  const {
    loading: loadingSendNotification,
    success: successSendNotification,
    error: errorSendNotification,
  } = useSelector(
    (state: any): State<any> => state.notification.sendNotification
  );

  /**
   * Formik handling
   */
  const notificationForm = useFormik<SendNotificationRequest>({
    enableReinitialize: true,
    initialValues: {
      audience: [],
      title: "",
      arabicTitle: "",
      body: "",
      arabicBody: "",
      scheduleDate: undefined,
      occurrence: OccurrenceEnum.NOW,
      method: NotificationMethodEnum.WHATSAPP,
      templateId: null,
    },
    initialErrors: errorSendNotification,
    validationSchema: Yup.object<SendNotificationRequest>({
      audience: Yup.array()
        .min(
          1,
          t("minLengthArray", { minLengthArray: 1, displayName: "audience" })
        )
        .required(
          t("isNotEmpty", {
            displayName: "audience",
          })
        ),
      title: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "title" }))
            .max(50, t("maxLength", { maxLength: 50, displayName: "title" })),
      }),
      arabicTitle: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "arabicTitle" }))
            .max(
              50,
              t("maxLength", { maxLength: 50, displayName: "arabicTitle" })
            ),
      }),
      body: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "body" }))
            .max(255, t("maxLength", { maxLength: 255, displayName: "body" })),
      }),

      arabicBody: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "arabicBody" }))
            .max(
              255,
              t("maxLength", { maxLength: 255, displayName: "arabicBody" })
            ),
      }),
      method: Yup.string().required(t("isNotEmpty", { displayName: "method" })),
      templateId: Yup.string().when("method", {
        is: NotificationMethodEnum.WHATSAPP,
        then: (schema) =>
          schema.required(t("isNotEmpty", { displayName: "template" })),
      }),
      scheduleDate: Yup.string().required(
        t("isNotEmpty", {
          displayName: " ",
        })
      ),
    }),

    onSubmit: (values) => {
      const _audience = values.audience.map((v: any) => v.value);
      if (!_audience || _audience.length === 0) return;
      dispatch(
        sendNotification({
          ...values,
          audience: _audience,
          occurrence: nowOrLater,
          method: sendMethod,
        })
      );
    },
  });

  useEffect(() => {
    if (successSendNotification) {
      toast.success(t("notificationSentSuccessfully"), {
        autoClose: 3000,
      });
      dispatch(resetSendNotification());
      notificationForm.resetForm();
      setTimeout(() => {
        history.push("/notifications/list");
      }, 1000);
    }
  }, [successSendNotification]);

  useEffect(() => {
    notificationForm.setFieldValue("method", sendMethod);
  }, [sendMethod]);

  useEffect(() => {
    if (nowOrLater === OccurrenceEnum.NOW) {
      notificationForm.setFieldValue("scheduleDate", new Date());
    }
  }, [nowOrLater]);

  useEffect(() => {
    if (sendMethod === NotificationMethodEnum.WHATSAPP) {
      const _chooseTemplate = templates?.find(
        (t) => t.sid === selectedTemplate?.value
      );
      if (_chooseTemplate?.types["twilio/text"]) {
        notificationForm.setFieldValue(
          "body",
          _chooseTemplate?.types["twilio/text"]["body"]
        );
      }
      if (_chooseTemplate?.types["twilio/call-to-action"]) {
        notificationForm.setFieldValue(
          "body",
          _chooseTemplate?.types["twilio/call-to-action"]["body"]
        );
      }
      notificationForm.setFieldValue("templateId", _chooseTemplate?.sid);
    } else {
      notificationForm.setFieldValue("body", null);
      notificationForm.setFieldTouched("body", false);
    }
  }, [selectedTemplate, sendMethod]);

  //#endregion

  const onChangeDate = (event: any) => {
    if (event.length > 0) {
      const _scheduleDate = event[0];
      notificationForm.setFieldValue("scheduleDate", _scheduleDate);
    }
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("notifyUsers")} pageTitle={t("notifications")} />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      notificationForm.handleSubmit();
                      return false;
                    }}
                    action="#"
                  >
                    <Row className="justify-content-center mb-3">
                      <div className="mb-3 col-lg-6 mt-3">
                        <label className="form-label">{t("method")}</label>
                        <div>
                          <div className="form-check form-check-inline">
                            <Input
                              className="form-check-input"
                              type="radio"
                              name="inlineRadio4"
                              id="inlineRadio4"
                              value={NotificationMethodEnum.WHATSAPP}
                              checked={
                                sendMethod == NotificationMethodEnum.WHATSAPP
                              }
                              onChange={() => {
                                setSendMethod(NotificationMethodEnum.WHATSAPP);
                              }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="inlineRadio4"
                            >
                              {t(NotificationMethodEnum.WHATSAPP)}
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <Input
                              className="form-check-input"
                              type="radio"
                              name="inlineRadio3"
                              id="inlineRadio3"
                              value={NotificationMethodEnum.PUSH_NOTIFICATION}
                              checked={
                                sendMethod ==
                                NotificationMethodEnum.PUSH_NOTIFICATION
                              }
                              onChange={() => {
                                setSendMethod(
                                  NotificationMethodEnum.PUSH_NOTIFICATION
                                );
                              }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="inlineRadio3"
                            >
                              {t(NotificationMethodEnum.PUSH_NOTIFICATION)}
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="clearfix"></div>
                      {typeof errorSendNotification === "string" && (
                        <Label className="invalid-feedback d-block">
                          {errorSendNotification}
                        </Label>
                      )}
                      <div className="mb-3 col-lg-6">
                        <Label htmlFor="audience" className="form-label">
                          {t("audience")}
                          <span className="text-danger">*</span>
                        </Label>
                        <Select
                          id="audience"
                          placeholder={t("chooseAudience")}
                          closeMenuOnSelect={false}
                          value={notificationForm.values.audience}
                          onChange={(changedValue: any) => {
                            let filteredValue = changedValue;
                            if (
                              changedValue.find(
                                (v: any) =>
                                  v.value === NotificationAudienceEnum.STUDENT
                              ) &&
                              changedValue.find(
                                (v: any) =>
                                  v.value ===
                                  NotificationAudienceEnum.STUDENT_NO_PURCHASE
                              )
                            ) {
                              filteredValue = changedValue.filter(
                                (v: any) =>
                                  v.value !== NotificationAudienceEnum.STUDENT
                              );
                            }
                            if (
                              changedValue.find(
                                (v: any) =>
                                  v.value === NotificationAudienceEnum.PARENT
                              ) &&
                              changedValue.find(
                                (v: any) =>
                                  v.value ===
                                  NotificationAudienceEnum.PARENT_NO_PURCHASE
                              )
                            ) {
                              filteredValue = changedValue.filter(
                                (v: any) =>
                                  v.value !== NotificationAudienceEnum.PARENT
                              );
                            }
                            notificationForm.setFieldValue(
                              "audience",
                              filteredValue
                            );
                          }}
                          isMulti={true}
                          options={
                            [
                              {
                                label: t("allStudents"),
                                value: NotificationAudienceEnum.STUDENT,
                              },
                              {
                                label: t("allParents"),
                                value: NotificationAudienceEnum.PARENT,
                              },
                              {
                                label: t("allTeachers"),
                                value: NotificationAudienceEnum.TEACHER_ADMIN,
                              },
                              {
                                label: `${t("students")} (${t(
                                  "noPurchaseInCurrentSemester"
                                )})`,
                                value:
                                  NotificationAudienceEnum.STUDENT_NO_PURCHASE,
                              },
                              {
                                label: `${t("parents")} (${t(
                                  "noPurchaseInCurrentSemester"
                                )})`,
                                value:
                                  NotificationAudienceEnum.PARENT_NO_PURCHASE,
                              },
                            ] as any
                          }
                        />
                        {notificationForm.errors.audience ? (
                          <FormFeedback
                            type="invalid"
                            style={{ display: "block" }}
                          >
                            {notificationForm.errors.audience}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="clearfix"></div>
                      {sendMethod === NotificationMethodEnum.WHATSAPP && (
                        <>
                          <div className="mb-3 col-lg-6">
                            <div className="mb-3 col">
                              <TemplateSelect
                                placeholder="chooseTemplate"
                                isMulti={false}
                                closeMenuOnSelect={true}
                                value={selectedTemplate}
                                onChange={(newValue) => {
                                  setSelectedTemplate(newValue);
                                }}
                              ></TemplateSelect>
                            </div>
                          </div>
                        </>
                      )}
                      <div className="clearfix"></div>
                      {sendMethod != NotificationMethodEnum.WHATSAPP && (
                        <>
                          <div className="mb-3 col-lg-3">
                            <Label htmlFor="title" className="form-label">
                              {t("title")}
                              <span className="text-danger">*</span>
                            </Label>
                            <Input
                              type="text"
                              className="form-control"
                              id="title"
                              placeholder={t("enterTitle")}
                              value={notificationForm.values.title || ""}
                              onChange={notificationForm.handleChange}
                              onBlur={notificationForm.handleBlur}
                              invalid={
                                notificationForm.touched.title &&
                                notificationForm.errors.title
                                  ? true
                                  : false
                              }
                            />
                            {notificationForm.touched.title &&
                            notificationForm.errors.title ? (
                              <FormFeedback type="invalid">
                                {notificationForm.errors.title}
                              </FormFeedback>
                            ) : null}
                          </div>
                          <div className="mb-3 col-lg-3">
                            <Label htmlFor="arabicTitle" className="form-label">
                              {t("arabicTitle")}
                              <span className="text-danger">*</span>
                            </Label>
                            <Input
                              type="text"
                              className="form-control"
                              id="arabicTitle"
                              placeholder={t("enterArabicTitle")}
                              value={notificationForm.values.arabicTitle || ""}
                              onChange={notificationForm.handleChange}
                              onBlur={notificationForm.handleBlur}
                              invalid={
                                notificationForm.touched.arabicTitle &&
                                notificationForm.errors.arabicTitle
                                  ? true
                                  : false
                              }
                            />
                            {notificationForm.touched.arabicTitle &&
                            notificationForm.errors.arabicTitle ? (
                              <FormFeedback type="invalid">
                                {notificationForm.errors.arabicTitle}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </>
                      )}
                      <div className="clearfix"></div>
                      <div className="mb-3 col-lg-6">
                        <Label htmlFor="body" className="form-label">
                          {sendMethod === NotificationMethodEnum.WHATSAPP
                            ? t("Template")
                            : t("body")}
                          {sendMethod != NotificationMethodEnum.WHATSAPP && (
                            <span className="text-danger">*</span>
                          )}
                        </Label>
                        <Input
                          disabled={
                            sendMethod === NotificationMethodEnum.WHATSAPP
                          }
                          type="textarea"
                          row="5"
                          style={{ height: "150px" }}
                          className="form-control"
                          id="body"
                          placeholder={t("enterMessageBody")}
                          value={notificationForm.values.body || ""}
                          onChange={notificationForm.handleChange}
                          onBlur={notificationForm.handleBlur}
                          invalid={
                            notificationForm.touched.body &&
                            notificationForm.errors.body
                              ? true
                              : false
                          }
                        />
                        {notificationForm.touched.body &&
                        notificationForm.errors.body ? (
                          <FormFeedback type="invalid">
                            {notificationForm.errors.body}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="clearfix"></div>
                      {sendMethod != NotificationMethodEnum.WHATSAPP && (
                        <div className="mb-3 col-lg-6">
                          <Label htmlFor="arabicBody" className="form-label">
                            {t("arabicBody")}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="textarea"
                            row="2"
                            className="form-control"
                            id="arabicBody"
                            placeholder={t("enterMessageBodyInArabic")}
                            value={notificationForm.values.arabicBody || ""}
                            onChange={notificationForm.handleChange}
                            onBlur={notificationForm.handleBlur}
                            invalid={
                              notificationForm.touched.arabicBody &&
                              notificationForm.errors.arabicBody
                                ? true
                                : false
                            }
                          />
                          {notificationForm.touched.arabicBody &&
                          notificationForm.errors.arabicBody ? (
                            <FormFeedback type="invalid">
                              {notificationForm.errors.arabicBody}
                            </FormFeedback>
                          ) : null}
                        </div>
                      )}

                      <div className="clearfix"></div>
                      <div className="mb-3 col-lg-6">
                        <label className="form-label">{t("send")}</label>
                        <div>
                          <div className="form-check form-check-inline">
                            <Input
                              className="form-check-input"
                              type="radio"
                              name="inlineRadio1"
                              id="inlineRadio1"
                              value={OccurrenceEnum.NOW}
                              checked={nowOrLater === OccurrenceEnum.NOW}
                              onChange={() => {
                                setNowOrLater(OccurrenceEnum.NOW);
                                notificationForm.setFieldValue(
                                  "scheduleDate",
                                  new Date()
                                );
                              }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="inlineRadio1"
                            >
                              {t(OccurrenceEnum.NOW)}
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <Input
                              className="form-check-input"
                              type="radio"
                              name="inlineRadio2"
                              id="inlineRadio2"
                              checked={nowOrLater === OccurrenceEnum.LATER}
                              onChange={() => {
                                setNowOrLater(OccurrenceEnum.LATER);
                              }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="inlineRadio2"
                            >
                              {t(OccurrenceEnum.LATER)}
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="clearfix"></div>
                      {nowOrLater === "later" && (
                        <>
                          <div className="mb-3 col-lg-6">
                            <label
                              htmlFor="scheduledNotification"
                              className="form-label"
                            >
                              {t("scheduleDate")}
                              <span className="text-danger">*</span>
                            </label>
                            <Flatpickr
                              id="scheduleDate"
                              onChange={(e) => onChangeDate(e)}
                              className="form-control"
                              value={notificationForm.values?.scheduleDate}
                              options={{
                                mode: "single",
                                enableTime: true,
                                dateFormat: "Y/m/d H:i",
                              }}
                            />
                            {notificationForm.touched.scheduleDate &&
                            notificationForm.errors.scheduleDate ? (
                              <FormFeedback
                                type="invalid"
                                style={{ display: "block" }}
                              >
                                {notificationForm.errors.scheduleDate}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </>
                      )}
                      <div className="clearfix"></div>
                      <div className="col-lg-6">
                        <div className="hstack gap-2 justify-content-end">
                          <Link
                            to={"/notifications/list"}
                            className="btn btn-link link-success fw-medium"
                          >
                            {t("goToList")}
                          </Link>
                          <button
                            type="submit"
                            className="btn btn-success"
                            id="add-btn"
                            disabled={loadingSendNotification}
                          >
                            {loadingSendNotification ? (
                              <span className="d-flex align-items-center">
                                <span className="flex-grow-1 me-2">
                                  {t("sending")}...
                                </span>
                                <Spinner
                                  size="sm"
                                  className="flex-shrink-0"
                                  role="status"
                                >
                                  {" "}
                                  {t("sending")}
                                  ...{" "}
                                </Spinner>
                              </span>
                            ) : (
                              <>
                                {nowOrLater === "now" ? t("send") : t("later")}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="clearfix"></div>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default NotifyUsersPage;
